import {
  Component,
  ElementRef,
  Injector,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, from } from 'rxjs';
import { mergeMap, map } from 'rxjs/operators';

import { WidgetComponent, FormService } from '@alfresco/adf-core';

import { WkAlfrescoApplicationService } from '../../services/wk-alfresco-application.service';
import { WkProcessContentService } from '../../services/wk-process-content.service';
import { NodeEntry } from '@alfresco/js-api/typings/src/api/content-rest-api';
import { CloseAllModalsAction, ViewFileAction } from '../../store';
import { ProcessListService } from '../../components/shared/services/process-list.service';
import { Store } from '@ngrx/store';

const diffEvents = 'event($event)';

@Component({
  selector: 'wk-upload-widget',
  templateUrl: './wk-upload.widget.html',
  styleUrls: ['./wk-upload.widget.scss'],
  host: {
    '(click)': diffEvents,
    '(blur)': diffEvents,
    '(change)': diffEvents,
    '(focus)': diffEvents,
    '(focusin)': diffEvents,
    '(focusout)': diffEvents,
    '(input)': diffEvents,
    '(invalid)': diffEvents,
    '(select)': diffEvents,
  },
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WkUploadWidgetComponent extends WidgetComponent implements OnInit {
  hasFile: boolean;
  displayText: string;
  multipleOption = '';
  mimeTypeIcon: string;

  @ViewChild('uploadFiles')
  fileInput: ElementRef;
  previewLocation: any;
  private readonly store: Store;
  public wkProcessContentService: WkProcessContentService;
  private readonly wkApplicationService: WkAlfrescoApplicationService;
  private readonly processlistService: ProcessListService;
  private readonly route: ActivatedRoute;
  private readonly router: Router;

  constructor(
    private readonly injector: Injector,
    public formService: FormService
  ) {
    super(formService);
    this.store = this.injector.get<Store>(Store);
    this.wkApplicationService = this.injector.get<WkAlfrescoApplicationService>(
      WkAlfrescoApplicationService
    );
    this.wkProcessContentService = this.injector.get<WkProcessContentService>(
      WkProcessContentService
    );
    this.processlistService =
      this.injector.get<ProcessListService>(ProcessListService);
    this.route = this.injector.get<ActivatedRoute>(ActivatedRoute);
    this.router = this.injector.get<Router>(Router);
  }

  ngOnInit() {
    if (this.field && this.field.value && this.field.value.length > 0) {
      this.hasFile = true;
    }
    this.getMultipleFileParam();

    this.route.queryParams.subscribe((params) => {
      this.previewLocation = params['previewLocation'];
    });
  }

  onFileChanged(event: any) {
    const files = event.target.files;
    let filesSaved = [];

    if (this.field.json.value) {
      filesSaved = [...this.field.json.value];
    }

    if (files && files.length > 0) {
      from(files)
        .pipe(mergeMap((file) => this.uploadRawContent(file)))
        .subscribe({
          next: (res) => filesSaved.push(res),
          error: () =>
            this.wkApplicationService.logInstance.error(
              'Error uploading file. See console output for more details.'
            ),
          complete: () => {
            this.field.value = filesSaved;
            this.field.json.value = filesSaved;
            this.hasFile = true;
          },
        });
    }
  }

  private uploadRawContent(file: any): Observable<any> {
    return this.wkProcessContentService
      .createTemporaryRawRelatedContent(file)
      .pipe(
        map((response: any) => {
          this.wkApplicationService.logInstance.info(response);
          response.contentBlob = file;
          return response;
        })
      );
  }

  getMultipleFileParam() {
    if (this.field && this.field.params && this.field.params.multiple) {
      this.multipleOption = this.field.params.multiple ? 'multiple' : '';
    }
  }

  getIcon(mimeType: string): string {
    return this.wkApplicationService.thumbnailInstance.getMimeTypeIcon(
      mimeType
    );
  }

  onFileClicked(contentLinkModel: any): void {
    if (!this.router.url.includes('/preview') && !contentLinkModel.sourceId) {
      this.store.dispatch(
        new ViewFileAction(
          { entry: contentLinkModel } as NodeEntry,
          this.router.url !== '/all-docs-dashboard'
            ? contentLinkModel.parentId
            : null
        )
      );
      this.store.dispatch(new CloseAllModalsAction());
    } else {
      const nodeId = contentLinkModel.sourceId.split(';')[0];
      this.processlistService.onFileClicked.next(nodeId);
    }
  }
}
